import { useState, useMemo, useEffect, useCallback } from "react";
import { useClinic } from "~/providers/ClinicProvider";
import { ClinicConfig } from "~/enums/common";
import { useApi } from "providers/ApiProvider";
import { AcquisitionChannelsData } from "~/core/resources/customersApi";
import { useSimpleRegistrationContext } from "pages/SimpleRegistrationsPage";
import { Container } from "components/SimpleRegistrations/Container";
import { Header } from "components/SimpleRegistrations/Header";
import { Body } from "components/SimpleRegistrations/Body";
import { Footer } from "components/SimpleRegistrations/Footer";
import { SlideModal } from "components/Modal/SlideModal";
import { LandscapeBirthdayInput } from "components/SimpleRegistrations/landscape/BirthdayInput";
import { PortraitBirthdayInput } from "components/SimpleRegistrations/portrait/BirthdayInput";
import { LandscapeNameInput } from "components/SimpleRegistrations/landscape/NameInput";
import { PortraitNameInput } from "components/SimpleRegistrations/portrait/NameInput";
import { LandscapeAcquisitionChannelsSelect } from "components/SimpleRegistrations/landscape/AcquisitionChannelsSelect";
import { PortraitAcquisitionChannelsSelect } from "components/SimpleRegistrations/portrait/AcquisitionChannelsSelect";
import { InfoConfirm } from "components/SimpleRegistrations/landscape/InfoConfirm";
import { LandscapeVisitPurposeSelect } from "~/components/SimpleRegistrations/landscape/VisitPurposeSelect";
import { PortraitVisitPurposeSelect } from "~/components/SimpleRegistrations/portrait/VisitPurposeSelect";

interface CreateNewRegistrationModalProps {
  open: boolean;
  onClose: (registered?: boolean) => void;
}

export function CreateNewRegistrationModal({
  open,
  onClose,
}: CreateNewRegistrationModalProps) {
  const {
    isLandscape,
    setLoading,
    smsEnabled,
    page,
    setPage,
    birthday,
    name,
    acquisitionChannelsIds,
    visitPurpose,
  } = useSimpleRegistrationContext();
  const { clinicConfig } = useClinic();
  const { customersApi } = useApi();
  const [usePageIndicator, setUsePageIndicator] = useState(true);
  const [emptyFieldPages, setEmptyFieldPages] = useState<number[]>([]);
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [acquisitionChannels, setAcquisitionChannels] = useState<
    AcquisitionChannelsData[]
  >([]);

  const hasAcquisitionChannels = acquisitionChannels.length > 0;
  const acquisitionChannelsOffset = hasAcquisitionChannels ? 0 : 1;

  const loadAcquisitionChannels = useCallback(async () => {
    const res = await customersApi.getAcquisitionChannels({
      limit: 12,
      orderBy: "order desc",
      visible: true,
      registrationView: true,
    });
    const payload = await res.data;
    payload.data && setAcquisitionChannels(payload.data.slice(0, 12));
  }, [customersApi]);

  useEffect(() => {
    loadAcquisitionChannels();
  }, [loadAcquisitionChannels]);

  const useRegistrationPurpose = useMemo(() => {
    if (clinicConfig && clinicConfig.length > 0) {
      const find = clinicConfig.find(
        (item: { codeName: string }) =>
          item.codeName === ClinicConfig.useRegistrationPurpose
      );
      return find ? JSON.parse(find.codeValue || "false") : false;
    }
    return false;
  }, [clinicConfig]);

  const totalPages = useMemo(() => {
    return (useRegistrationPurpose ? 5 : 4) - acquisitionChannelsOffset;
  }, [useRegistrationPurpose, acquisitionChannelsOffset]);

  const isVisitPurposePage = useMemo(() => {
    return useRegistrationPurpose && page === 4 - acquisitionChannelsOffset;
  }, [useRegistrationPurpose, page, acquisitionChannelsOffset]);

  const infoConfirmPage = useMemo(() => {
    return (useRegistrationPurpose ? 5 : 4) - acquisitionChannelsOffset;
  }, [useRegistrationPurpose, acquisitionChannelsOffset]);

  useEffect(() => {
    if (open) {
      const emptyPages = [];

      // 비어있는 필드 확인하고 해당 페이지 번호 저장
      if (birthday === "") emptyPages.push(1);
      if (name === "") emptyPages.push(2);
      if (!acquisitionChannelsIds || acquisitionChannelsIds.length === 0)
        emptyPages.push(3);
      if (
        useRegistrationPurpose &&
        (!visitPurpose || visitPurpose.length === 0)
      )
        emptyPages.push(4);

      // 만약 모든 필드가 채워져 있다면, 확인 페이지로
      if (emptyPages.length === 0) {
        emptyPages.push(infoConfirmPage);
        setUsePageIndicator(false);
      }

      setEmptyFieldPages(emptyPages);
      // 첫 번째 비어있는 페이지로 이동
      setCurrentPageIndex(0);
      setPage(emptyPages[0] || 1);
    }
  }, [open]);

  const handleChangePrevPage = () => {
    const prevIndex = currentPageIndex - 1;
    if (prevIndex >= 0) {
      setCurrentPageIndex(prevIndex);
      setPage(emptyFieldPages[prevIndex]);
      setUsePageIndicator(emptyFieldPages[prevIndex] !== infoConfirmPage);
    } else {
      // 첫 페이지에서 이전을 누르면 모달 닫기
      onClose();
    }
  };

  const handleNextPage = () => {
    const nextIndex = currentPageIndex + 1;
    if (nextIndex < emptyFieldPages.length) {
      setCurrentPageIndex(nextIndex);
      setPage(emptyFieldPages[nextIndex]);
    } else {
      setUsePageIndicator(false);
      setPage(infoConfirmPage);
    }
  };

  return (
    <SlideModal open={open} disableSlide={true}>
      <Container>
        <Header
          onChangePrevPage={handleChangePrevPage}
          onClose={onClose}
          usePageIndicator={usePageIndicator}
          page={page}
          total={totalPages}
        />
        <Body>
          {page === 1 &&
            (isLandscape ? (
              <LandscapeBirthdayInput onClickNext={handleNextPage} />
            ) : (
              <PortraitBirthdayInput onClickNext={handleNextPage} />
            ))}
          {page === 2 &&
            (isLandscape ? (
              <LandscapeNameInput onClickNext={handleNextPage} />
            ) : (
              <PortraitNameInput onClickNext={handleNextPage} />
            ))}
          {page === 3 &&
            hasAcquisitionChannels &&
            (isLandscape ? (
              <LandscapeAcquisitionChannelsSelect
                acquisitionChannels={acquisitionChannels}
              />
            ) : (
              <PortraitAcquisitionChannelsSelect
                acquisitionChannels={acquisitionChannels}
              />
            ))}
          {isVisitPurposePage &&
            (isLandscape ? (
              <LandscapeVisitPurposeSelect />
            ) : (
              <PortraitVisitPurposeSelect />
            ))}
          {page === infoConfirmPage && <InfoConfirm />}
        </Body>
        {page >= 3 && page < infoConfirmPage && (
          <Footer
            text="다음"
            onClick={handleNextPage}
            disabled={
              (hasAcquisitionChannels &&
                (!acquisitionChannelsIds ||
                  acquisitionChannelsIds.length === 0)) ||
              (isVisitPurposePage &&
                (!visitPurpose || visitPurpose.length === 0))
            }
          />
        )}
        {page === infoConfirmPage && (
          <Footer
            text="접수하기"
            disabled={!smsEnabled}
            onClick={() => {
              onClose(true);
              setLoading(true);
            }}
          />
        )}
      </Container>
    </SlideModal>
  );
}
